import { useAPIQuery } from "@dev2_techwalnut/tw-api-query-hook";
import { useEffect, useRef } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

const ReturnDailyPlanMaterialModal = ({ productionData }) => {
    const navigate = useNavigate();
    const closeBtnRef = useRef(null);

    const handleCloseModal = () => {
        if (closeBtnRef.current) {
            closeBtnRef.current.click();
            navigate(-1);
        }
    };

    const { useFetch: useFetchRetrunSlip, useCreate: useCreateReturnSlip } =
        useAPIQuery({
            key: "api/return/slip",
            endpoint: "/api/return/slip/",
        });

    const { data: retrunSlip, isLoading: loadingReturnSlip } =
        useFetchRetrunSlip({
            params: { daily_plan_id: productionData?.id },
            enabled: !!productionData,
        });

    const { mutate: createReturnSlip, isPending } = useCreateReturnSlip({
        successMessageFromServer: true,
        errorMessageFromServer: true,
        onSuccess: () => {
            handleCloseModal();
        },
    });

    const {
        handleSubmit,
        register,
        formState: { errors },
        control,
        reset,
        watch,
        getValues,
        setValue,
    } = useForm({ mode: "onBlur" });

    const { fields, insert, remove } = useFieldArray({
        control,
        name: "return",
    });

    const watchReturn = watch("return") || [];

    const onSubmit = (data) => {
        const res =
            data?.return
                ?.map((r) => ({
                    requisition_id: r?.requisition_id,
                    batch_id: r?.batch?.value ?? null,
                    return_quantity: Number(r?.remaining_quantity || 0),
                }))
                ?.filter((r) => r.return_quantity > 0) ?? [];

        Swal.fire({
            title: "Are you sure?",
            text: "Do you want to return this material?",
            icon: "warning",
            confirmButtonText: "Yes",
            denyButtonText: `No, cancel!`,
            showDenyButton: true,
        }).then((result) => {
            if (result.isConfirmed) {
                createReturnSlip({
                    data: res.length
                        ? {
                              returns: res,
                          }
                        : {
                              daily_plan_id: productionData?.id,
                          },
                });
            }
        });
    };

    useEffect(() => {
        if (retrunSlip?.data) {
            reset({
                return: retrunSlip.data.map((rs) => ({
                    item_id: rs.item_id,
                    item_name: rs.item_name,
                    received_quantity: rs.received_quantity,
                    utilized_quantity: rs.utilized_quantity,
                    is_surplus_production: rs.is_surplus_production,
                    production_material_variance:
                        rs.production_material_variance,
                    requisition_id: rs.requisition_id,
                    batches: rs.batches,
                    max_remaining_quantity: rs.remaining_quantity,
                    remaining_quantity: 0,
                    batch: null,
                })),
            });
        }
    }, [retrunSlip, reset]);

    useEffect(() => {
        return () => {
            const backdrop = document.querySelector(".modal-backdrop");
            if (backdrop) {
                backdrop.remove();
                document.body.classList.remove("modal-open");
                document.body.style.cssText = "";
            }
        };
    }, []);

    const getGroupRows = (index) => {
        const all = watchReturn;
        const current = all?.[index];
        if (!current) return [];

        return all
            .map((row, idx) => ({ ...row, _idx: idx }))
            .filter((row) => row.requisition_id === current.requisition_id);
    };

    const handleAddRowBelow = (index) => {
        const currentRow = getValues(`return.${index}`);
        if (!currentRow) return;

        const newRow = {
            ...currentRow,
            batch: null,
            remaining_quantity: 0,
        };

        insert(index + 1, newRow);
    };

    const handleRemoveRow = (index) => {
        remove(index);
    };

    return (
        <div
            className="modal fade m-0 p-0"
            tabIndex={-1}
            id="retrun-dp-material-modal"
            aria-modal
        >
            <div className="modal-dialog modal-xl" role="document">
                <div className="modal-content">
                    <button
                        type="button"
                        className="close"
                        data-bs-dismiss="modal"
                        ref={closeBtnRef}
                    >
                        <em
                            className="icon ni ni-cross"
                            aria-hidden="true"
                        ></em>
                    </button>
                    <div className="modal-header">
                        <h5 className="modal-title">Close daily plan</h5>
                    </div>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="modal-body">
                            <div className="table-responsive">
                                <table className="table table-sm align-middle">
                                    <thead>
                                        <tr>
                                            <th style={{ width: "20%" }}>
                                                Item
                                            </th>
                                            <th
                                                style={{ width: "15%" }}
                                                className="text-end"
                                            >
                                                Received
                                            </th>
                                            <th
                                                style={{ width: "15%" }}
                                                className="text-end"
                                            >
                                                Utilized
                                            </th>
                                            <th
                                                style={{ width: "16%" }}
                                                className="text-end"
                                            >
                                                Batches
                                            </th>
                                            <th
                                                style={{ width: "14%" }}
                                                className="text-end"
                                            >
                                                Remaining
                                            </th>
                                            <th
                                                style={{ width: "15%" }}
                                                className="text-end"
                                            >
                                                Variance
                                            </th>
                                            <th
                                                style={{ width: "10%" }}
                                                className="text-center"
                                            >
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {fields.map((r, index) => {
                                            const groupRows =
                                                getGroupRows(index);
                                            const allBatchesForMaterial =
                                                r?.batches ?? [];
                                            const disableRemoveButton =
                                                groupRows.length === 1;
                                            const maxRemaining =
                                                r?.max_remaining_quantity ??
                                                r?.remaining_quantity ??
                                                0;

                                            const selectedBatchIdsInGroup =
                                                groupRows
                                                    .filter(
                                                        (row) =>
                                                            row._idx !==
                                                                index &&
                                                            row?.batch?.value !=
                                                                null
                                                    )
                                                    .map(
                                                        (row) => row.batch.value
                                                    );

                                            const batchOptions =
                                                allBatchesForMaterial
                                                    .filter(
                                                        (b) =>
                                                            !selectedBatchIdsInGroup.includes(
                                                                b.batch_id
                                                            )
                                                    )
                                                    .map((b) => ({
                                                        value: b.batch_id,
                                                        label: `${
                                                            b.batch_number
                                                        } (returnable: ${
                                                            b.returnable_quantity ??
                                                            b.allotted_quantity
                                                        })`,
                                                        allotted_quantity:
                                                            b.returnable_quantity ??
                                                            b.allotted_quantity,
                                                    })) ?? [];

                                            const allBatchIdsForMaterial =
                                                allBatchesForMaterial.map(
                                                    (b) => b.batch_id
                                                );
                                            const selectedBatchIdsForMaterial =
                                                groupRows
                                                    .map(
                                                        (row) =>
                                                            row?.batch?.value
                                                    )
                                                    .filter(Boolean);

                                            const allBatchesSelected =
                                                allBatchIdsForMaterial.length >
                                                    0 &&
                                                allBatchIdsForMaterial.every(
                                                    (id) =>
                                                        selectedBatchIdsForMaterial.includes(
                                                            id
                                                        )
                                                );

                                            const disableAddButton =
                                                maxRemaining <= 0 ||
                                                allBatchesSelected ||
                                                allBatchesForMaterial.length ===
                                                    0;

                                            return (
                                                <tr key={r.id}>
                                                    <td>{r.item_name}</td>

                                                    <td className="text-end">
                                                        {r.received_quantity}
                                                    </td>

                                                    <td className="text-end">
                                                        {r.utilized_quantity}
                                                    </td>

                                                    <td className="text-end">
                                                        <Controller
                                                            name={`return.${index}.batch`}
                                                            rules={{
                                                                validate: (
                                                                    value
                                                                ) =>
                                                                    maxRemaining <=
                                                                        0 ||
                                                                    !!value ||
                                                                    "Batch is required",
                                                            }}
                                                            control={control}
                                                            render={({
                                                                field,
                                                            }) => (
                                                                <>
                                                                    <Select
                                                                        {...field}
                                                                        options={
                                                                            batchOptions
                                                                        }
                                                                        isLoading={
                                                                            loadingReturnSlip
                                                                        }
                                                                        isClearable
                                                                        styles={{
                                                                            menuPortal:
                                                                                (
                                                                                    base
                                                                                ) => ({
                                                                                    ...base,
                                                                                    zIndex: 9999,
                                                                                }),
                                                                            menu: (
                                                                                base
                                                                            ) => ({
                                                                                ...base,
                                                                                zIndex: 9999,
                                                                            }),
                                                                        }}
                                                                        menuPortalTarget={
                                                                            document.body
                                                                        }
                                                                        placeholder="Select a batch"
                                                                        menuPlacement="auto"
                                                                        menuPosition="fixed"
                                                                        isDisabled={
                                                                            maxRemaining <=
                                                                            0
                                                                        }
                                                                        onChange={(
                                                                            selected
                                                                        ) => {
                                                                            field.onChange(
                                                                                selected
                                                                            );

                                                                            const currentValues =
                                                                                getValues(
                                                                                    "return"
                                                                                ) ||
                                                                                [];
                                                                            const currentRow =
                                                                                currentValues[
                                                                                    index
                                                                                ];

                                                                            if (
                                                                                selected
                                                                            ) {
                                                                                const batchMax =
                                                                                    Math.min(
                                                                                        maxRemaining,
                                                                                        Number(
                                                                                            selected?.allotted_quantity ||
                                                                                                0
                                                                                        )
                                                                                    );

                                                                                const group =
                                                                                    getGroupRows(
                                                                                        index
                                                                                    );
                                                                                const otherTotal =
                                                                                    group
                                                                                        .filter(
                                                                                            (
                                                                                                r
                                                                                            ) =>
                                                                                                r._idx !==
                                                                                                index
                                                                                        )
                                                                                        .reduce(
                                                                                            (
                                                                                                sum,
                                                                                                r
                                                                                            ) =>
                                                                                                sum +
                                                                                                Number(
                                                                                                    r.remaining_quantity ||
                                                                                                        0
                                                                                                ),
                                                                                            0
                                                                                        );

                                                                                // Max allowed AFTER subtracting other rows
                                                                                const autoMax =
                                                                                    Math.min(
                                                                                        batchMax,
                                                                                        maxRemaining -
                                                                                            otherTotal
                                                                                    );

                                                                                const currentQty =
                                                                                    Number(
                                                                                        currentRow?.remaining_quantity ||
                                                                                            0
                                                                                    );

                                                                                if (
                                                                                    !currentQty ||
                                                                                    currentQty ===
                                                                                        0
                                                                                ) {
                                                                                    setValue(
                                                                                        `return.${index}.remaining_quantity`,
                                                                                        autoMax
                                                                                    );
                                                                                }
                                                                            } else {
                                                                                setValue(
                                                                                    `return.${index}.remaining_quantity`,
                                                                                    0
                                                                                );
                                                                            }
                                                                        }}
                                                                    />
                                                                    {errors
                                                                        ?.return?.[
                                                                        index
                                                                    ]?.batch
                                                                        ?.message && (
                                                                        <span className="fs-8 text-danger">
                                                                            {
                                                                                errors
                                                                                    ?.return?.[
                                                                                    index
                                                                                ]
                                                                                    ?.batch
                                                                                    ?.message
                                                                            }
                                                                        </span>
                                                                    )}
                                                                </>
                                                            )}
                                                        />
                                                    </td>

                                                    <td className="text-end">
                                                        <input
                                                            type="number"
                                                            step="0.00001"
                                                            disabled={
                                                                maxRemaining <=
                                                                0
                                                            }
                                                            className="form-control form-control-sm text-end"
                                                            style={{
                                                                minWidth:
                                                                    "110px",
                                                            }}
                                                            {...register(
                                                                `return.${index}.remaining_quantity`,
                                                                {
                                                                    valueAsNumber: true,
                                                                    disabled:
                                                                        maxRemaining <=
                                                                        0,

                                                                    validate: (
                                                                        value
                                                                    ) => {
                                                                        const num =
                                                                            Number(
                                                                                value ||
                                                                                    0
                                                                            );
                                                                        if (
                                                                            num <
                                                                            0
                                                                        )
                                                                            return "Minimum allowed is 0";

                                                                        const selected =
                                                                            getValues(
                                                                                `return.${index}.batch`
                                                                            );

                                                                        // ⬅️ Per-batch maximum limit
                                                                        const batchMax =
                                                                            Math.min(
                                                                                maxRemaining,
                                                                                Number(
                                                                                    selected?.allotted_quantity ||
                                                                                        0
                                                                                )
                                                                            );

                                                                        if (
                                                                            num >
                                                                            batchMax
                                                                        ) {
                                                                            return `Max allowed for this batch is ${batchMax}`;
                                                                        }

                                                                        // ⬅️ Combined total validation
                                                                        const group =
                                                                            getGroupRows(
                                                                                index
                                                                            );
                                                                        const otherTotal =
                                                                            group
                                                                                .filter(
                                                                                    (
                                                                                        r
                                                                                    ) =>
                                                                                        r._idx !==
                                                                                        index
                                                                                )
                                                                                .reduce(
                                                                                    (
                                                                                        sum,
                                                                                        r
                                                                                    ) =>
                                                                                        sum +
                                                                                        Number(
                                                                                            r.remaining_quantity ||
                                                                                                0
                                                                                        ),
                                                                                    0
                                                                                );

                                                                        if (
                                                                            num +
                                                                                otherTotal >
                                                                            maxRemaining
                                                                        ) {
                                                                            return `Total for this material cannot exceed ${maxRemaining}`;
                                                                        }

                                                                        return true;
                                                                    },
                                                                }
                                                            )}
                                                        />

                                                        {errors?.return?.[index]
                                                            ?.remaining_quantity && (
                                                            <small className="text-danger d-block text-start">
                                                                {
                                                                    errors
                                                                        .return[
                                                                        index
                                                                    ]
                                                                        .remaining_quantity
                                                                        .message
                                                                }
                                                            </small>
                                                        )}
                                                    </td>

                                                    <td className="text-end">
                                                        {r.is_surplus_production &&
                                                        r.production_material_variance ? (
                                                            <span className="badge bg-warning text-dark text-wrap">
                                                                Extra:{" "}
                                                                {
                                                                    r
                                                                        .production_material_variance
                                                                        .surplus_production_quantity
                                                                }
                                                                <br />
                                                                Mat:{" "}
                                                                {
                                                                    r
                                                                        .production_material_variance
                                                                        .surplus_required_quantity
                                                                }
                                                                <br />
                                                                Short:{" "}
                                                                {
                                                                    r
                                                                        .production_material_variance
                                                                        .shortage_quantity
                                                                }
                                                            </span>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </td>

                                                    <td className="text-center">
                                                        <div className="btn-group btn-group-sm">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary"
                                                                onClick={() =>
                                                                    handleAddRowBelow(
                                                                        index
                                                                    )
                                                                }
                                                                disabled={
                                                                    disableAddButton
                                                                }
                                                                title={
                                                                    disableAddButton
                                                                        ? "All batches used"
                                                                        : "Add another batch row"
                                                                }
                                                            >
                                                                +
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-danger"
                                                                onClick={() =>
                                                                    handleRemoveRow(
                                                                        index
                                                                    )
                                                                }
                                                                disabled={
                                                                    disableRemoveButton
                                                                }
                                                                title={
                                                                    disableRemoveButton
                                                                        ? "At least one row must remain"
                                                                        : "Remove this row"
                                                                }
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="modal-footer bg-light">
                            <button
                                type="button"
                                className="btn btn-danger"
                                data-bs-dismiss="modal"
                                disabled={isPending}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isPending}
                            >
                                {isPending ? "Submitting" : "Submit"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ReturnDailyPlanMaterialModal;
